import React, { useState } from "react";
import { useTranslation } from "react-i18next";
import "./AuthPage.css";
import { LoginRequest } from "../types";
import { config, getActiveEnvironment, setActiveEnvironment, type Environment } from "../config";

type AuthPageProps = {
  onSubmit: (loginRequest: LoginRequest) => void;
  onMicrosoftLogin: () => void;
  error?: string | null;
  isLoading?: boolean;
};

const ENV_LABELS: Record<Environment, string> = {
  production: "Production",
  staging: "Staging",
  local: "Local",
};

const AVAILABLE_ENVS: Environment[] = [
  "production",
  "staging",
  ...(import.meta.env.DEV ? (["local"] as Environment[]) : []),
];

export function AuthPage({ onSubmit, onMicrosoftLogin, error, isLoading }: AuthPageProps) {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [clickCount, setClickCount] = useState(0);
  const [showEnvPicker, setShowEnvPicker] = useState(false);
  const { t, i18n } = useTranslation();

  const activeEnv = getActiveEnvironment();

  const handleTitleClick = () => {
    const next = clickCount + 1;
    setClickCount(next);
    if (next >= 10) {
      setShowEnvPicker(true);
    }
  };

  return (
    <div className="dibretto-pane">
      <div>
        <h1 className="info-title" onClick={handleTitleClick} style={{ userSelect: "none" }}>
          {t("auth.welcome")}
        </h1>

        {activeEnv !== "production" && (
          <span className={`env-badge env-badge--${activeEnv}`}>{ENV_LABELS[activeEnv]}</span>
        )}

        {showEnvPicker && (
          <div className="env-picker">
            {AVAILABLE_ENVS.map((env) => (
              <button
                key={env}
                className={`env-picker-btn ${activeEnv === env ? "env-picker-btn--active" : ""}`}
                onClick={() => setActiveEnvironment(env)}
              >
                {ENV_LABELS[env]}
              </button>
            ))}
          </div>
        )}

        <p className="info-block">{t("auth.loginText")}</p>

        <button
          type="button"
          onClick={onMicrosoftLogin}
          className="auth-button"
          style={{
            marginBottom: "10px",
            background: "#fff",
            color: "#000",
            border: "1px solid #ccc",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            gap: "8px",
          }}
          disabled={isLoading}
        >
          <svg
            width="16"
            height="16"
            viewBox="0 0 21 21"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
          >
            <rect x="1" y="1" width="9" height="9" fill="#F25022" />
            <rect x="11" y="1" width="9" height="9" fill="#7FBA00" />
            <rect x="1" y="11" width="9" height="9" fill="#00A4EF" />
            <rect x="11" y="11" width="9" height="9" fill="#FFB900" />
          </svg>
          {t("auth.microsoftButton")}
        </button>

        <div className="auth-or-divider">
          <span>{t("auth.orDivider")}</span>
        </div>

        <form
          className="auth-form"
          onSubmit={(e) => {
            e.preventDefault();
            onSubmit({ email, password, language: i18n.language });
          }}
        >
          <label htmlFor="user-identifier" className="auth-field-label">
            {t("auth.label.email")}
          </label>
          <input
            id="user-identifier"
            name="email"
            type="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            className="auth-input"
            disabled={isLoading}
            autoComplete="email"
          />

          <label htmlFor="user-secret" className="auth-field-label">
            {t("auth.label.password")}
          </label>
          <input
            id="user-secret"
            name="password"
            type="password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            className="auth-input"
            disabled={isLoading}
            autoComplete="current-password"
          />

          {error && <p className="error-message">{error}</p>}

          <button
            type="submit"
            className="auth-button"
            style={{ marginBottom: "10px" }}
            disabled={isLoading}
          >
            {isLoading ? `${t("auth.submit")}...` : t("auth.submit")}
          </button>
        </form>

        <p>
          <span
            dangerouslySetInnerHTML={{
              __html: t("auth.forgetPassword", { link: `${config.appUrl}/forgot-password` }),
            }}
          />
        </p>

        <p>
          {t("auth.noAccount")}
          <br />
          <a href="https://dibretto.com/request-account" target="_blank" rel="noreferrer">
            dibretto.com/request-account
          </a>
        </p>
      </div>
    </div>
  );
}
